import { z } from 'zod';
import { localizedStringSchema } from '../localized-string.js';
import {
  embedSettingsSchema,
  locationSchema,
  priceSchema,
  questionSchema,
  refundPolicySchema,
} from './booking.js';
import { emailSchema, localeSchema, objectIdSchema, timeZoneSchema } from './common.js';

// What an anonymous booker can see about a host and an event type.

export const publicHostSchema = z.object({
  username: z.string(),
  /** The person. Shown under the business, or as it when there is no business. */
  name: z.string(),
  organizationName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  locale: localeSchema,
  timezone: z.string(),
});
export type PublicHost = z.infer<typeof publicHostSchema>;

export const publicEventTypeSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: localizedStringSchema,
  description: localizedStringSchema.optional(),
  colour: z.string(),
  durationMinutes: z.number().int(),
  location: locationSchema,
  questions: z.array(questionSchema),
  price: priceSchema.nullable(),
  /** True when the price is settled at the appointment rather than here. */
  payOnArrival: z.boolean().default(false),
  refundPolicy: refundPolicySchema,
  maxDaysAhead: z.number().int(),
  /** Whether payment is currently possible (host's Stripe account is live). */
  bookable: z.boolean(),
  /**
   * Cloudflare Turnstile site key, when a challenge applies to this event
   * type. Null on paid ones, which Stripe Checkout already protects, and
   * wherever Turnstile is not configured.
   */
  turnstileSiteKey: z.string().nullable(),
  /** The booker must confirm a code sent to their address before booking. */
  requiresEmailVerification: z.boolean(),
  /** Appearance when embedded on the host's site; the widget reads it live. */
  embed: embedSettingsSchema,
});
export type PublicEventType = z.infer<typeof publicEventTypeSchema>;

export const publicProfileSchema = z.object({
  host: publicHostSchema,
  eventTypes: z.array(publicEventTypeSchema),
});

export const slotsQuerySchema = z.object({
  /** ISO date (YYYY-MM-DD) in the booker's timezone. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  tz: timeZoneSchema,
  /** Needed to see times on an invitation-only event type. */
  inviteCode: z.string().min(8).max(128).optional(),
});

export const slotsResponseSchema = z.object({
  /** UTC ISO start instants. Client groups by day in its own timezone. */
  slots: z.array(z.string().datetime()),
  /**
   * The host connected a calendar that cannot be checked right now, so nothing
   * can be offered. The page says bookings are paused rather than showing an
   * empty month. A host with no calendar at all is never in this state.
   */
  calendarBlocked: z.boolean(),
});
export type SlotsResponse = z.infer<typeof slotsResponseSchema>;

export const holdInputSchema = z.object({
  start: z.string().datetime(),
  /** Needed to hold a slot on an invitation-only event type. */
  inviteCode: z.string().min(8).max(128).optional(),
  /**
   * A hold this same booker is giving up, from a previous choice they have
   * come back from or reloaded past. Without it their own abandoned hold
   * reads as somebody else's and they are told the slot is taken.
   */
  releaseHoldId: objectIdSchema.optional(),
});
export const holdResponseSchema = z.object({
  holdId: z.string(),
  /**
   * What this booking will actually be, now that somebody has been chosen.
   *
   * Only different from the event type's own setting when that is `video`, and
   * that is the whole point: the list of times cannot name a tool because
   * nobody is assigned yet, but by the time a slot is held the answer exists.
   * The customer is told before they fill the form in, not in the confirmation.
   */
  location: locationSchema,
  /** Who they will be seeing. Empty is never expected; it means a missing account. */
  agentName: z.string(),
  expiresAt: z.string().datetime(),
});

/** Giving a slot back: the booker pressed back, or left the form. */
export const releaseHoldInputSchema = z.object({
  holdId: objectIdSchema,
  inviteCode: z.string().min(8).max(128).optional(),
});

export const answerSchema = z.object({
  questionId: z.string(),
  value: z.union([z.string().max(2000), z.boolean()]),
});

export const createBookingInputSchema = z.object({
  start: z.string().datetime(),
  holdId: z.string().optional(),
  idempotencyKey: z.string().uuid(),
  booker: z.object({
    name: z.string().trim().min(1).max(120),
    email: emailSchema,
    phone: z.string().trim().max(40).optional(),
    timezone: timeZoneSchema,
    locale: localeSchema,
  }),
  answers: z.array(answerSchema).max(20).default([]),
  notes: z.string().trim().max(2000).optional(),
  /** Honeypot: must be empty. */
  website: z.string().max(0).optional(),
  /** Cloudflare Turnstile result, when the page was given a site key. */
  turnstileToken: z.string().max(2048).optional(),
  /** Code from the verification email, when the host requires one. */
  emailCode: z.string().max(10).optional(),
  /** Single-use link this booking came from, for an invitation-only event type. */
  inviteCode: z.string().min(8).max(128).optional(),
  /**
   * Proof from the LINE MINI App that this customer is a known LINE user of
   * this shop. Verified server-side; when it holds, the booking records their
   * LINE user id and later messages can reach them there.
   */
  lineToken: z.string().max(2048).optional(),
  confirmDuplicate: z.boolean().optional(),
});
export type CreateBookingInput = z.infer<typeof createBookingInputSchema>;

export const bookingStatusSchema = z.enum([
  'held',
  'awaiting_payment',
  'confirmed',
  'cancelled',
  'completed',
  'no_show',
]);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

export const paymentStatusSchema = z.enum([
  'unpaid',
  'paid',
  'refunded',
  'partially_refunded',
  'failed',
]);

/** Booking as seen by the booker through their manage token. */
export const publicBookingSchema = z.object({
  id: z.string(),
  status: bookingStatusSchema,
  start: z.string().datetime(),
  end: z.string().datetime(),
  hostTimezone: z.string(),
  host: publicHostSchema,
  eventType: z.object({
    slug: z.string(),
    name: localizedStringSchema,
    durationMinutes: z.number().int(),
    location: locationSchema,
    refundPolicy: refundPolicySchema,
    afterBooking: z.object({
      kind: z.enum(['confirmation_page', 'redirect']),
      message: localizedStringSchema.nullable().optional(),
      redirectUrl: z.string().optional(),
    }),
    /** False once the event type was deleted; reschedule is then disabled. */
    exists: z.boolean(),
    /** Whether the booker may reschedule right now, and why not otherwise. */
    rescheduleAllowed: z.boolean(),
    rescheduleBlockedReason: z.enum(['limit', 'not_allowed', 'too_late', 'unavailable']).nullable(),
    reschedulesLeft: z.number().int(),
  }),
  booker: z.object({
    name: z.string(),
    email: z.string(),
    timezone: z.string(),
    locale: localeSchema,
  }),
  joinUrl: z.string().nullable(),
  /**
   * The host's calendar has already sent this booker an invitation, so the
   * appointment is in their calendar without them doing anything. The manage
   * page uses it to stop offering to do a thing that is already done.
   */
  calendarInvited: z.boolean(),
  /**
   * Whether the booker may cancel. The host's switch, read live, so turning it
   * off reaches manage links that are already out there.
   */
  cancelAllowed: z.boolean(),
  payment: z
    .object({
      amount: z.number().int(),
      currency: z.string(),
      status: paymentStatusSchema,
      receiptUrl: z.string().nullable(),
      refundedAmount: z.number().int(),
    })
    .nullable(),
  cancellation: z
    .object({ by: z.enum(['booker', 'host', 'system']), reason: z.string().nullable() })
    .nullable(),
  /** Set for paid bookings still awaiting Checkout completion. */
  checkoutUrl: z.string().nullable(),
});
export type PublicBooking = z.infer<typeof publicBookingSchema>;

export const createBookingResponseSchema = z.object({
  bookingId: z.string(),
  token: z.string(),
  status: bookingStatusSchema,
  checkoutUrl: z.string().nullable(),
});
export type CreateBookingResponse = z.infer<typeof createBookingResponseSchema>;

export const cancelBookingInputSchema = z.object({
  reason: z.string().trim().max(1000).optional(),
});
export const rescheduleBookingInputSchema = z.object({ start: z.string().datetime() });

// ---------------------------------------------------------------------------
// Host-side views
// ---------------------------------------------------------------------------

export const hostBookingSummarySchema = z.object({
  id: z.string(),
  status: bookingStatusSchema,
  start: z.string().datetime(),
  end: z.string().datetime(),
  eventTypeId: z.string(),
  eventTypeSlug: z.string(),
  eventTypeName: localizedStringSchema,
  eventTypeColour: z.string().nullable(),
  bookerName: z.string(),
  bookerEmail: z.string(),
  /**
   * The workspace member taking it, or null when that is the account owner.
   *
   * Null for the overwhelming majority of bookings, and deliberately so: a page
   * that is not shared has nobody to name, and showing the owner their own name
   * on every row would be noise.
   */
  agent: z
    .object({
      id: z.string(),
      name: z.string(),
      /**
       * Whether they are still in the workspace.
       *
       * False when they have left or deleted their account, which leaves an
       * appointment nobody is going to keep. It is the owner's to fix — by
       * handing it to somebody else — so the screens say it rather than letting
       * it pass as an ordinary booking.
       */
      inWorkspace: z.boolean().default(true),
    })
    .nullable()
    .default(null),
  source: z.enum(['public', 'manual', 'reschedule']),
  syncStatus: z.enum(['none', 'pending', 'synced', 'failed', 'missing_externally']),
  /** Mail to this booker was permanently refused; they never got their confirmation. */
  bookerEmailFailed: z
    .object({
      at: z.string().datetime(),
      kind: z.enum(['bounced', 'complained']),
      diagnostic: z.string().nullable(),
    })
    .nullable(),
  paymentStatus: paymentStatusSchema.nullable(),
  amount: z.number().int().nullable(),
  currency: z.string().nullable(),
  /**
   * Whether the viewer may act on this, as opposed to only look at it.
   *
   * False for a member's own appointment in somebody else's workspace. The
   * calendar needs it to know whether dragging the block means anything:
   * without it a member could drag their own booking around and be answered
   * with a 404 from a route that was never theirs.
   */
  canManage: z.boolean().default(true),
});
export type HostBookingSummary = z.infer<typeof hostBookingSummarySchema>;

export const hostBookingListSchema = z.object({
  items: z.array(hostBookingSummarySchema),
  nextCursor: z.string().nullable(),
});

export const hostBookingListQuerySchema = z.object({
  view: z.enum(['upcoming', 'past', 'cancelled']).default('upcoming'),
  eventTypeId: z.string().optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/**
 * What the calendar asks for: everything between two instants.
 *
 * A range rather than a page. A week is a small answer, and a grid cannot draw
 * half of one — "the next fifty" is a list's idea, not a calendar's.
 */
export const calendarQuerySchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
  /** One person's day, which is also what adds their busy time to the answer. */
  agentId: objectIdSchema.optional(),
  eventTypeId: z.string().optional(),
});

/** Time that is spoken for without saying what for. */
export const busyBlockSchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
  /** Whose day it is gone from. */
  agentId: z.string(),
  /**
   * What it is, which is only ever filled in for the person whose calendar it
   * is. Somebody else's dentist appointment is an hour that is taken; the word
   * "dentist" belongs to them.
   */
  title: z.string().nullable(),
});

/** When somebody has said they are working, for this business. */
export const workingBandSchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
  agentId: z.string(),
});

export const calendarSchema = z.object({
  bookings: z.array(hostBookingSummarySchema),
  busy: z.array(busyBlockSchema),
  /**
   * The hours each person has given this business, so an owner can see at a
   * glance that one of them covers mornings and another the afternoons.
   *
   * Said rather than deduced: it comes from the hours they set in Business
   * Box, not from their calendar, and it costs nothing to send — no provider
   * is asked anything.
   */
  working: z.array(workingBandSchema),
  /**
   * Public holidays in the window, for the countries this business observes.
   *
   * Named, not merely closed. "Nothing can be booked on the twelfth" is a
   * puzzle; "Sports Day" is an answer, and in Japan it is the answer somebody
   * is actually looking for — the calendar is full of them and they move.
   */
  holidays: z.array(z.object({ date: z.string(), name: localizedStringSchema })),
  /**
   * Time the viewer has taken out of their own week, as opposed to time they
   * never worked. Only this tells the screen whether there is anything to put
   * back: a Sunday and a blocked Monday both look shut from outside.
   */
  blocked: z.array(z.object({ start: z.string().datetime(), end: z.string().datetime() })),
  /**
   * The people whose days are in this answer, in the order to draw them. The
   * viewer is first; nobody is listed at all when they work alone.
   */
  people: z.array(z.object({ id: z.string(), name: z.string() })),
  /**
   * A calendar we could not read, so the busy list is thinner than the truth.
   * The screen says so rather than implying a free afternoon.
   */
  busyUnverified: z.boolean(),
});
export type CalendarView = z.infer<typeof calendarSchema>;
export type BusyBlock = z.infer<typeof busyBlockSchema>;

export const hostBookingSchema = hostBookingSummarySchema.extend({
  /**
   * Whether the viewer may act on this booking, as opposed to only read it.
   *
   * False for a workspace member looking at an appointment in somebody else's
   * business: it is their meeting, but cancelling it moves the owner's money.
   * Sent so the screen can leave those buttons out rather than offer actions
   * that answer 404.
   */
  canManage: z.boolean().default(true),
  hostTimezone: z.string(),
  booker: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    locale: localeSchema,
    timezone: z.string(),
  }),
  location: locationSchema,
  answers: z.array(
    z.object({
      questionId: z.string(),
      label: localizedStringSchema,
      value: z.union([z.string(), z.boolean()]),
    }),
  ),
  bookerNotes: z.string().nullable(),
  hostNotes: z.string().nullable(),
  joinUrl: z.string().nullable(),
  syncError: z.string().nullable(),
  cancellation: z
    .object({
      by: z.enum(['booker', 'host', 'system']),
      reason: z.string().nullable(),
      at: z.string().datetime(),
    })
    .nullable(),
  payment: z
    .object({
      amount: z.number().int(),
      currency: z.string(),
      status: paymentStatusSchema,
      receiptUrl: z.string().nullable(),
      refundedAmount: z.number().int(),
      refunds: z.array(
        z.object({
          amount: z.number().int(),
          reason: z.string(),
          at: z.string().datetime(),
          status: z.string(),
        }),
      ),
    })
    .nullable(),
  rescheduledFromId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type HostBooking = z.infer<typeof hostBookingSchema>;

export const hostCancelInputSchema = z.object({
  message: z.string().trim().max(1000).optional(),
  /**
   * What to do with a paid booking's money. Defaults to refunding it, which is
   * the fair answer when the appointment is called off by the person who was
   * meant to give it — but it is the host's money and their policy to honour,
   * so it is their choice to make rather than ours to make silently.
   */
  refund: z.enum(['full', 'none']).default('full'),
});
export const hostNotesInputSchema = z.object({ hostNotes: z.string().max(5000) });

export const manualBookingInputSchema = z.object({
  eventTypeId: z.string(),
  start: z.string().datetime(),
  booker: z.object({
    name: z.string().trim().min(1).max(120),
    email: emailSchema,
    phone: z.string().trim().max(40).optional(),
    locale: localeSchema.default('en'),
    timezone: timeZoneSchema,
  }),
  notes: z.string().trim().max(2000).optional(),
  /**
   * Who takes it. Omitted means the rotation decides, which is what the booking
   * page would have done; naming somebody is the host overruling it for one
   * appointment, and they must be on that event type.
   */
  agentId: objectIdSchema.optional(),
  /** Skip the conflict check (double-book deliberately). */
  force: z.boolean().default(false),
  /** Send the booker the usual confirmation email. */
  notify: z.boolean().default(true),
});
export type ManualBookingInput = z.infer<typeof manualBookingInputSchema>;

export const refundPreviewSchema = z.object({
  amount: z.number().int(),
  currency: z.string(),
  percent: z.number().int(),
});
export type RefundPreview = z.infer<typeof refundPreviewSchema>;
